// client/src/components/Layout.jsx
// Logged-in app shell. Sticky translucent header with brand wordmark,
// chip-nav, and a user menu (avatar + name + Super Admin badge + logout).
//
// `user` comes from the parent (App.jsx pulls it from useMe to avoid two
// hook calls in the tree). Logout uses useLogout which clears cache and
// hard-redirects, so no manual invalidate here.

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLogout } from '../hooks/useMe.js';
import LogoWordmark from './LogoWordmark.jsx';
import VersionFooter from './VersionFooter.jsx';

// Lets a page register a "menu opener" so the global header (next to the logo)
// can show a hamburger that opens THAT page's drawer. CampDetailPage registers
// its section drawer; other pages leave it null and the hamburger stays hidden.
const PageMenuContext = createContext(null);
export function usePageMenu() {
  return useContext(PageMenuContext) || { setOpener: () => {} };
}

export default function Layout({ user, children }) {
  const [opener, setOpener] = useState(null);
  return (
    <PageMenuContext.Provider value={{ setOpener }}>
      <div className="min-h-screen flex flex-col">
        <Header user={user} opener={opener} />
        <main className="flex-1 mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 lg:px-8 animate-fade-in">
          {children}
        </main>
        <VersionFooter />
      </div>
    </PageMenuContext.Provider>
  );
}

function Header({ user, opener }) {
  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-ink-200/70">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3 sm:gap-4">
        {opener && (
          <button
            type="button"
            onClick={opener}
            aria-label="פתיחת תפריט"
            className="sm:hidden -ms-1 w-10 h-10 grid place-items-center rounded-xl text-ink-600 hover:bg-ink-100 hover:text-ink-900 transition-colors"
          >
            <HamburgerIcon className="w-6 h-6" />
          </button>
        )}
        <Link to="/" aria-label="TicTac · עמוד הבית" className="block shrink-0">
          <LogoWordmark className="h-10 w-auto" />
        </Link>
        <Nav />
        <div className="ms-auto">
          <UserMenu user={user} />
        </div>
      </div>
    </header>
  );
}

function HamburgerIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function Nav() {
  // Top-level nav uses chip pills (smaller than tab style) so the header
  // stays light and doesn't compete with the camp-detail subnav below.
  const loc = useLocation();
  const links = [{ to: '/', label: 'תכניות' }];
  return (
    <nav className="hidden sm:flex items-center gap-1.5 text-sm">
      {links.map((l) => {
        const active = loc.pathname === l.to;
        return (
          <Link
            key={l.to}
            to={l.to}
            className={active ? 'chip-active' : 'chip'}
          >
            {l.label}
          </Link>
        );
      })}
    </nav>
  );
}

function UserMenu({ user }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const logout = useLogout();

  // Close when clicking outside or hitting Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) return null;
  const displayName = user.name || user.email.split('@')[0];
  const initials = (user.name || user.email)
    .split(/\s+|@|\.|-/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join('')
    .toUpperCase();

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2.5 rounded-full ps-1 pe-3 py-1
                   border border-ink-200 bg-white hover:bg-ink-50
                   transition-colors"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {user.picture ? (
          <img
            src={user.picture}
            alt=""
            className="h-8 w-8 rounded-full bg-ink-200 object-cover"
          />
        ) : (
          <div className="h-8 w-8 rounded-full bg-brand-gradient text-white text-xs font-bold grid place-items-center">
            {initials || '·'}
          </div>
        )}
        <div className="hidden sm:flex flex-col items-start leading-tight">
          <span className="text-sm font-semibold text-ink-900">{displayName}</span>
          {user.isSuperAdmin && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-brand-600">Super Admin</span>
          )}
        </div>
        <ChevronDownIcon className={`w-4 h-4 text-ink-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute end-0 mt-2 w-64 card-pop p-1.5 animate-slide-up origin-top"
        >
          <div className="px-3 py-2 border-b border-ink-100">
            <div className="text-sm font-semibold text-ink-900">{displayName}</div>
            <div className="text-xs text-ink-500 break-all" dir="ltr">{user.email}</div>
            {user.isSuperAdmin && (
              <span className="badge-indigo mt-1.5">Super Admin</span>
            )}
          </div>
          {user.isSuperAdmin && (
            <Link
              to="/admin/card-orders"
              onClick={() => setOpen(false)}
              className="block w-full mt-1 rounded-xl px-3 py-2 text-sm font-semibold text-start
                         text-ink-700 hover:bg-brand-50 hover:text-brand-700 transition-colors"
              role="menuitem"
            >
              ניהול הזמנות כרטיסים
            </Link>
          )}
          <button
            type="button"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
            className="w-full mt-1 rounded-xl px-3 py-2 text-sm font-semibold text-start
                       text-joy-700 hover:bg-joy-50 transition-colors
                       disabled:opacity-50"
            role="menuitem"
          >
            {logout.isPending ? 'מתנתק...' : 'התנתק'}
          </button>
        </div>
      )}
    </div>
  );
}

function ChevronDownIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
    </svg>
  );
}
